import {
  Component,
  OnInit,
  ViewChildren,
  QueryList
} from '@angular/core';
import {
  UserService,
  CampusWard,
  WardMeeting,
  SortService,
  WardLeader,
  WardPosition,
  IComponentHasChanges,
  CampusWardsDataService,
  CampusWardDetailsForUpdate
} from '../../shared';
import { LeaderComponent } from '../leader.component';
import { WardLeaderFormComponent } from '../leader-form/ward-leader-form.component';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  FormControl
} from '@angular/forms';
import * as Config from '../../shared/tokens/constants.tokens';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-ward-information',
  templateUrl: './ward-information.component.html',
  styleUrls: ['./ward-information.component.scss']
})
export class WardInformationComponent extends LeaderComponent
  implements OnInit, IComponentHasChanges {
  @ViewChildren(WardLeaderFormComponent)
  leaderForms: QueryList<WardLeaderFormComponent>;

  ward: CampusWard;
  details: CampusWardDetailsForUpdate;
  leaders: WardLeader[];
  loading = false;
  loadingError = false;
  saving = false;
  saved = false;
  showPassword = false;

  wardForm: FormGroup;

  constructor(
    protected userService: UserService,
    protected dialog: MatDialog,
    private wardService: CampusWardsDataService,
    private sorter: SortService,
    private formBuilder: FormBuilder
  ) {
    super(userService, dialog);
  }

  ngOnInit() {
    super.ngOnInit();
    this.wardForm = this.formBuilder.group({
      bishopOffice: ['', [Validators.maxLength(50)]],
      phone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      // As of 2018-3-6, the email validator assumed it was a required field.  This let's us around that.
      email: [
        '',
        [
          Validators.maxLength(70),
          Validators.pattern(Config.Constants.emailRegex)
        ]
      ],
      meetings: this.formBuilder.array([]),
      password: new FormControl('', [Validators.required])
    });

    // TODO if not proxied, load the ward details that the leader is in charge of
    if (this.isWardLeader()) {
      this.loadWardDetails(this.getWard());
    }
  }

  checkWardSelected() {
    return this.ward != null;
  }

  /**
   * Indicates whether or not there are unsaved changes on the leader.
   */
  hasUnsavedChanges(): boolean {
    return (
      this.wardHasUnsavedChanges() ||
      this.getFirstLeaderFormWithUnsavedChanges() != null
    );
  }

  /**
   * Gets a message letting the user know where their unsaved changes are.
   */
  get unsavedMessage(): string {
    const leaderWithUnsaved = this.getFirstLeaderFormWithUnsavedChanges();
    if (leaderWithUnsaved != null) {
      return `You have unsaved changes to the ${
        leaderWithUnsaved.leader.position
      }'s information.  Are you sure you want to continue?`;
    } else if (this.wardHasUnsavedChanges()) {
      return 'You have unsaved changes to the ward\'s informaion.  Are you sure you want to continue?';
    }

    return null;
  }

  /**
   * Update the ward that we're working with when the admin
   */
  onWardSelected(ward: CampusWard) {
    if (ward) {
      this.loadWardDetails(ward);
    }
  }

  /**
   * Saves the ward information.
   */
  saveWardInformation() {
    if (this.saving || !this.wardForm.dirty || !this.wardForm.valid) {
      return;
    }

    // get our data from the form
    const ward = Object.assign({}, this.details, this.wardForm.value);

    this.saved = false; // reset this flag

    this.saving = true;
    this.wardService
      .updateWardInformation(ward)
      .pipe(
        finalize(() => (this.saving = false))
      )
      .subscribe(
        updatedWard => {
          this.handleDetails(updatedWard, true);
          this.saved = true;
        },
        error => {
          this.showError(error);
        }
      );
  }

  /**
   * Loads the ward's information for our user to view and edit.
   */
  private loadWardDetails(ward: CampusWard) {
    this.ward = ward;
    this.loading = true;
    this.wardService
      .getCampusWardDetailsForUpdate(ward.campusWardId)
      .pipe(
        finalize(() => (this.loading = false))
      )
      .subscribe(
        details => this.handleDetails(details),
        error => (this.loadingError = true)
      );
  }

  /**
   * When we load the details, we need to reset our form and patch in all the values.
   */
  private handleDetails(
    wardDetails: CampusWardDetailsForUpdate,
    preserveLeaders = false
  ) {
    this.saved = false;
    this.details = wardDetails;
    this.details.meetings.sort(this.sorter.sortWardMeetingsAsc);
    this.populateForm();

    if (!preserveLeaders) {
      // get our leaders ready for updates as well
      this.leaders = [];
      if (this.details) {
        if (this.details.bishop) {
          this.leaders.push(this.details.bishop);
        } else {
          this.leaders.push(
            new WardLeader(WardPosition.Bishop, this.details.campusWardId)
          );
        }

        if (this.details.firstCounselor) {
          this.leaders.push(this.details.firstCounselor);
        } else {
          this.leaders.push(
            new WardLeader(
              WardPosition.FirstCounselor,
              this.details.campusWardId
            )
          );
        }

        if (this.details.secondCounselor) {
          this.leaders.push(this.details.secondCounselor);
        } else {
          this.leaders.push(
            new WardLeader(
              WardPosition.SecondCounselor,
              this.details.campusWardId
            )
          );
        }
      }
    }
  }

  /**
   * Populates our form from the ward's details.
   */
  private populateForm() {
    // reset the form
    this.wardForm.reset();

    // clear any meetings that might have been there before
    const meetings = <FormArray>this.wardForm.get('meetings');
    const toRemove = meetings.length;
    for (let i = 0; i < toRemove; i++) {
      meetings.removeAt(0);
    }

    // add a form group for each meeting
    for (const meeting of this.details.meetings) {
      meetings.push(this.getFormGroupForMeeting(meeting));
    }

    // patch in everything else; we already "patched" in the meetings
    this.wardForm.patchValue(this.details);
    this.wardForm.get('password').patchValue(this.details.password);
  }

  /**
   * Creates a form group for the meeting, setting the default value
   */
  private getFormGroupForMeeting(meeting: WardMeeting): FormGroup {
    return this.formBuilder.group({
      time: [
        meeting.time,
        [
          Validators.required,
          Validators.pattern(/^\d{1,2}:\d{2} {0,1}[AapP][mM]$|^TBD$/)
        ]
      ],
      location: [meeting.location, [Validators.required]],
      type: meeting.meetingType.description,
      campusWardMeetingId: meeting.campusWardMeetingId // set this so that we preserve the key
    });
  }

  /**
   * Gets the first leader form that has unsaved changes; null if none have unsaved changes.
   */
  private getFirstLeaderFormWithUnsavedChanges(): WardLeaderFormComponent {
    const unsaved = this.leaderForms.find(form => form.hasUnsavedChanges());
    return unsaved !== undefined ? unsaved : null;
  }

  /**
   * Whether or not we have unsaved changes in the ward information itself.
   */
  private wardHasUnsavedChanges() {
    return this.wardForm.dirty;
  }

  /**
   * Show or hide password
   */
  togglePassoWordVisibilty() {
    if (this.showPassword) {
      this.showPassword = false;
    } else {
      this.showPassword = true;
    }
  }
}
